import { useEffect, useRef, useState } from 'react'
import { Card, Alert, Badge, Button, Drawer, Typography, Spin } from 'antd'
import { MessageOutlined, ReloadOutlined } from '@ant-design/icons'
import { useMessagesWithRealtime } from '../../hooks/useRealtimeMessages'
import MessageList from './MessageList'
import MessageInput from './MessageInput'
import MessageDetail from './MessageDetail'
import type { MessageWithAttachments } from '../../hooks/useRealtimeMessages'

const { Title, Text } = Typography

interface MessageThreadProps {
  reportId: string
  reportCaseId?: string
  isAdmin?: boolean
  userName?: string
  userRole?: string
  className?: string
  showHeader?: boolean
  height?: string | number
}

export default function MessageThread({
  reportId,
  reportCaseId,
  isAdmin = false,
  userName,
  userRole,
  className = '',
  showHeader = true,
  height = '600px'
}: MessageThreadProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [selectedMessage, setSelectedMessage] = useState<MessageWithAttachments | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const {
    messages,
    loading,
    error,
    sendMessage,
    markAsRead,
    refetch,
    unreadCount
  } = useMessagesWithRealtime(reportId)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages])

  // Mark messages as read when viewed by admin
  useEffect(() => {
    if (isAdmin && messages.length > 0) {
      messages
        .filter(m => !m.is_admin_message && !m.read_at)
        .forEach(m => markAsRead(m.id))
    }
  }, [messages, isAdmin, markAsRead])

  const handleSendMessage = async (content: string) => {
    const result = await sendMessage(content, {
      isAdmin,
      senderName: userName,
      senderRole: userRole,
      metadata: {
        reportCaseId,
        timestamp: new Date().toISOString()
      }
    })

    return result
  }

  const handleMessageClick = (message: MessageWithAttachments) => {
    setSelectedMessage(message)
    setDrawerOpen(true)
  }

  if (error) {
    return (
      <Alert
        message="Error loading messages"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={refetch}>
            <ReloadOutlined /> Retry
          </Button>
        }
      />
    )
  }

  return (
    <>
      <Card 
        className={`message-thread ${className}`}
        bodyStyle={{ 
          height: typeof height === 'number' ? `${height}px` : height,
          display: 'flex',
          flexDirection: 'column',
          padding: 0
        }}
      >
        {showHeader && (
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageOutlined className="text-lg" />
                <Title level={5} className="mb-0">Messages</Title>
                {unreadCount > 0 && (
                  <Badge count={unreadCount} />
                )}
              </div>
              {reportCaseId && (
                <Text type="secondary" className="text-sm">
                  Case: {reportCaseId}
                </Text>
              )}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-4 py-2">
          {loading && messages.length === 0 ? (
            <div className="flex justify-center items-center h-full">
              <Spin size="large" />
            </div>
          ) : (
            <>
              <MessageList
                messages={messages}
                loading={loading}
                onMessageClick={handleMessageClick}
                currentUserId={userName}
              />
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        <MessageInput
          onSend={handleSendMessage}
          loading={loading}
          placeholder={isAdmin ? "Reply to reporter..." : "Send a message..."}
          allowAttachments={false} // Will be enabled in Week 3 Day 3
        />
      </Card>

      <Drawer
        title="Message Details"
        placement="right"
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        width={400}
      >
        {selectedMessage && (
          <MessageDetail
            message={selectedMessage}
            onClose={() => setDrawerOpen(false)}
          />
        )}
      </Drawer>
    </>
  )
}